import type {
  ScriptSummary,
  Script,
  PublicScript,
  SaveResult,
  SaveStaleError,
  ScratchSaveResult,
  TemplateKey,
  ScriptStatus,
  ScriptFeatures,
  CommentThread,
  CreateThreadRequest,
  AddReplyRequest,
  Suggestion,
  PublicSuggestion,
  ScriptVersion,
  ScriptVersionFull,
  AIDraftResponse,
  Recording,
  RecordingDetail,
  QuestionMarker,
  BundleKind,
  AdminBundleSummary,
  AdminBundleDetail,
  AdminBundleItemInput,
  ClientBundle,
} from '../../shared/types';

import type { SearchResult } from '../../shared/search';
import type {
  CutdownCreateResult,
  CutdownInfo,
  CutdownLength,
  CutdownSuggestion,
} from '../../shared/cutdown';
import type { NoteEntry, PublicNoteEntry } from '../../shared/clientNotes';
import type { ReviewChecklistMode, ReviewChecklistSetting } from '../../shared/reviewChecklist';
import { getOrCreateDeviceToken } from './deviceToken';

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

/** Server error copy is written for clients (e.g. the rate-limit message) —
 *  show it verbatim. Anything else (network drop, aborted request) carries a
 *  raw browser string like "Failed to fetch", so use a human line instead. */
export function clientErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.message && err.message !== 'Request failed') {
    return err.message;
  }
  return fallback;
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let message = 'Request failed';
    let body: unknown;
    try {
      body = await res.json();
      const err = (body as { error?: string })?.error;
      if (err) message = err;
    } catch {
      // ignore
    }
    throw new ApiError(res.status, message, body);
  }
  return (await res.json()) as T;
}

export const authApi = {
  status: () =>
    api<{ needsSetup: boolean }>('/api/auth/status'),
  me: () => api<{ ok: boolean }>('/api/auth/me'),
  setup: (password: string, setupSecret: string) =>
    api<{ ok: true }>('/api/auth/setup', {
      method: 'POST',
      body: JSON.stringify({ password, setup_secret: setupSecret }),
    }),
  login: (password: string, code?: string) =>
    api<{ ok: true }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ password, code }),
    }),
  logout: () => api<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  // ---------- Two-factor (TOTP) ----------
  totpStatus: () =>
    api<{ enabled: boolean; pending: boolean }>('/api/auth/totp'),
  totpSetup: () =>
    api<{ secret: string; otpauth_url: string }>('/api/auth/totp/setup', {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  totpEnable: (code: string) =>
    api<{ ok: true }>('/api/auth/totp/enable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  totpDisable: (password: string, code: string) =>
    api<{ ok: true }>('/api/auth/totp/disable', {
      method: 'POST',
      body: JSON.stringify({ password, code }),
    }),

  // Recent errors for the /admin/security panel.
  listErrors: () =>
    api<{
      errors: Array<{
        id: number;
        created_at: number;
        source: string;
        message: string;
        stack: string | null;
        context: string | null;
      }>;
    }>('/api/auth/errors'),

  // Run an on-demand D1 → R2 backup. Same code path as the weekly cron.
  runBackup: () =>
    api<{ ok: true }>('/api/auth/backup/run', {
      method: 'POST',
      body: JSON.stringify({}),
    }),
};

export const adminApi = {
  searchScripts: (
    query: string,
    opts: { view: 'active' | 'archived'; project_id?: string; template_key?: TemplateKey; signal?: AbortSignal },
  ) => {
    const params = new URLSearchParams({ q: query, view: opts.view });
    if (opts.project_id) params.set('project_id', opts.project_id);
    if (opts.template_key) params.set('template_key', opts.template_key);
    return api<{ results: SearchResult[]; has_more: boolean }>(`/api/admin/search?${params}`, {
      signal: opts.signal,
    });
  },
  listScripts: (opts?: { view?: 'active' | 'archived' | 'trashed' }) => {
    const view = opts?.view ?? 'active';
    const qs = view === 'active' ? '' : `?view=${view}`;
    return api<{ scripts: ScriptSummary[] }>(`/api/admin/scripts${qs}`);
  },
  createScript: (body: {
    template_key: TemplateKey;
    title: string;
    client_name: string;
    project_name?: string;
    /** Dashboard slug — links this project to the per-client dashboard at
     *  dashboard.sheahanstudios.com/[slug]. Optional. */
    dashboard_slug?: string;
    /** Optional pre-built doc JSON (used by AI drafting flow). */
    doc_json?: string;
  }) =>
    api<{ id: string; slug: string }>('/api/admin/scripts', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  draftFromTranscript: (transcript: string) =>
    api<AIDraftResponse>('/api/admin/transcripts/draft', {
      method: 'POST',
      body: JSON.stringify({ transcript }),
    }),
  getScript: (id: string) => api<Script>(`/api/admin/scripts/${id}`),

  // saveScript explicitly handles 409 (stale) without throwing.
  async saveScript(
    id: string,
    body: { expected_version: number; doc_json: string; title: string },
  ): Promise<SaveResult | SaveStaleError> {
    const res = await fetch(`/api/admin/scripts/${id}/save`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.status === 409) {
      return (await res.json()) as SaveStaleError;
    }
    if (!res.ok) {
      const errBody = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(res.status, errBody.error ?? 'Save failed');
    }
    return (await res.json()) as SaveResult;
  },

  setStatus: (id: string, status: ScriptStatus) =>
    api<{ ok: true; status: ScriptStatus }>(`/api/admin/scripts/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),

  /** Update (or clear) the parent project's dashboard slug. Affects every
   *  script that shares this project — surface that scope clearly in
   *  whatever UI calls this. Pass `null` to clear. */
  setProjectDashboardSlug: (projectId: string, dashboardSlug: string | null) =>
    api<{ ok: true; dashboard_slug: string | null }>(
      `/api/admin/projects/${projectId}/dashboard-slug`,
      {
        method: 'PATCH',
        body: JSON.stringify({ dashboard_slug: dashboardSlug }),
      },
    ),

  /** Update (or clear) this script's "ready to review" nudge target
   *  (Phase B3) — script-scoped, not project-scoped. Pass `null` to clear. */
  setScriptClientEmail: (scriptId: string, clientEmail: string | null) =>
    api<{ ok: true; client_email: string | null }>(
      `/api/admin/scripts/${scriptId}/client-email`,
      {
        method: 'PATCH',
        body: JSON.stringify({ client_email: clientEmail }),
      },
    ),

  /** Per-script "What we need from you" checklist (C8). Omit `prompts` to
   *  keep the stored custom prompts; custom with none saves as off. */
  setScriptReviewChecklist: (
    scriptId: string,
    setting: { mode: ReviewChecklistMode; prompts?: string[] },
  ) =>
    api<{ ok: true; review_checklist: ReviewChecklistSetting }>(
      `/api/admin/scripts/${scriptId}/review-checklist`,
      { method: 'PATCH', body: JSON.stringify(setting) },
    ),

  /** Every general note on the script, newest first, "Earlier notes" last. */
  listScriptNotes: (scriptId: string) =>
    api<{ notes: NoteEntry[] }>(`/api/admin/scripts/${scriptId}/notes`),

  deleteScriptNote: (scriptId: string, noteId: string) =>
    api<{ ok: true }>(`/api/admin/scripts/${scriptId}/notes/${noteId}`, { method: 'DELETE' }),

  /** Update (or clear) this script's review-page approval deadline.
   *  Pass `null` to clear. */
  setScriptApproveBy: (scriptId: string, approveBy: string | null) =>
    api<{ ok: true; approve_by: string | null }>(
      `/api/admin/scripts/${scriptId}/approve-by`,
      {
        method: 'PATCH',
        body: JSON.stringify({ approve_by: approveBy }),
      },
    ),

  // Soft delete by default (script lands in Trash). Pass permanent: true
  // from the Trash tab to drop the row irreversibly.
  deleteScript: (id: string, opts?: { permanent?: boolean }) =>
    api<{ ok: true; permanent: boolean }>(
      `/api/admin/scripts/${id}${opts?.permanent ? '?permanent=1' : ''}`,
      { method: 'DELETE' },
    ),

  restoreScript: (id: string) =>
    api<{ ok: true }>(`/api/admin/scripts/${id}/restore`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  setArchived: (id: string, archived: boolean) =>
    api<{ ok: true; archived: boolean }>(`/api/admin/scripts/${id}/archive`, {
      method: 'POST',
      body: JSON.stringify({ archived }),
    }),

  duplicateScript: (id: string) =>
    api<{ id: string; slug: string }>(`/api/admin/scripts/${id}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  // Cutdown helper (2D): nothing is written until createCutdowns.
  getCutdownInfo: (id: string, signal?: AbortSignal) =>
    api<CutdownInfo>(`/api/admin/scripts/${id}/cutdown`, { signal }),

  suggestCutdown: (id: string, seconds: CutdownLength, signal?: AbortSignal) =>
    api<CutdownSuggestion>(`/api/admin/scripts/${id}/cutdown/suggest`, {
      method: 'POST',
      body: JSON.stringify({ seconds }),
      signal,
    }),

  createCutdowns: (
    id: string,
    docVersion: number,
    cuts: Array<{ seconds: CutdownLength; keep: number[] }>,
  ) =>
    api<CutdownCreateResult>(`/api/admin/scripts/${id}/cutdown/create`, {
      method: 'POST',
      body: JSON.stringify({ doc_version: docVersion, cuts }),
    }),

  rotateSlug: (id: string) =>
    api<{ ok: true; slug: string }>(`/api/admin/scripts/${id}/rotate-slug`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  listVersions: (id: string) =>
    api<{ versions: ScriptVersion[] }>(`/api/admin/scripts/${id}/versions`),
  getVersion: (id: string, versionId: string) =>
    api<{ version: ScriptVersionFull }>(`/api/admin/scripts/${id}/versions/${versionId}`),
  saveVersion: (id: string, note?: string) =>
    api<{ ok: true; id: string }>(`/api/admin/scripts/${id}/versions`, {
      method: 'POST',
      body: JSON.stringify({ note: note ?? '' }),
    }),
  restoreVersion: (id: string, versionId: string) =>
    api<{ ok: true; doc_version: number }>(
      `/api/admin/scripts/${id}/versions/${versionId}/restore`,
      { method: 'POST', body: JSON.stringify({}) },
    ),

  // saveScratch mirrors saveScript: 409 (stale) is returned, not thrown.
  async saveScratch(
    id: string,
    body: { base_version: number; scratch_json: string },
    opts?: { keepalive?: boolean },
  ): Promise<ScratchSaveResult | SaveStaleError> {
    const res = await fetch(`/api/admin/scripts/${id}/scratch`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: opts?.keepalive === true,
    });
    if (res.status === 409) {
      return (await res.json()) as SaveStaleError;
    }
    if (!res.ok) {
      const errBody = (await res.json().catch(() => ({}))) as { error?: string };
      throw new ApiError(res.status, errBody.error ?? 'Scratchpad save failed');
    }
    return (await res.json()) as ScratchSaveResult;
  },

  setScratchOpen: (id: string, open: boolean) =>
    api<{ ok: true; scratch_open: boolean }>(`/api/admin/scripts/${id}/scratch-open`, {
      method: 'PATCH',
      body: JSON.stringify({ open }),
    }),

  setFeatures: (id: string, features: ScriptFeatures) =>
    api<{ ok: true; features: ScriptFeatures }>(`/api/admin/scripts/${id}/features`, {
      method: 'PATCH',
      body: JSON.stringify({ features }),
    }),

  getPresence: (id: string) =>
    api<{ last_heartbeat_at: number | null }>(`/api/admin/scripts/${id}/presence`),

  getActiveSuggestion: (id: string) =>
    api<{ suggestion: Suggestion | null }>(`/api/admin/scripts/${id}/suggestion`),

  acceptSuggestion: (id: string, suggestionId: string) =>
    api<{ ok: true; doc_version: number }>(
      `/api/admin/scripts/${id}/suggestion/${suggestionId}/accept`,
      { method: 'POST', body: JSON.stringify({}) },
    ),

  rejectSuggestion: (id: string, suggestionId: string, note: string) =>
    api<{ ok: true }>(`/api/admin/scripts/${id}/suggestion/${suggestionId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  resetSuggestion: (id: string) =>
    api<{ reset: boolean }>(`/api/admin/scripts/${id}/suggestion/reset`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  markSuggestionReviewed: (id: string, suggestionId: string) =>
    api<{ ok: true }>(`/api/admin/scripts/${id}/suggestion/${suggestionId}/mark-reviewed`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  // ---------- Recordings (Phase 2A) ----------
  //
  // Upload uses multipart/form-data so the audio Blob streams as a binary
  // body rather than a base64-bloated JSON string. The other recording
  // calls go through the normal JSON helper.
  uploadRecording: async (
    scriptId: string,
    payload: {
      audio: Blob;
      mime_type: string;
      duration_ms: number | null;
      question_markers: QuestionMarker[];
    },
  ): Promise<{ ok: true; id: string }> => {
    const fd = new FormData();
    // Filename gives R2 a hint when the recording is browsed via the
    // dashboard; the worker derives the canonical extension from mime_type.
    const ext = payload.mime_type.startsWith('audio/mp4') ? 'm4a' : 'webm';
    fd.append('audio', payload.audio, `take.${ext}`);
    fd.append('mime_type', payload.mime_type);
    if (payload.duration_ms !== null) {
      fd.append('duration_ms', String(Math.round(payload.duration_ms)));
    }
    fd.append('question_markers', JSON.stringify(payload.question_markers));

    const res = await fetch(`/api/admin/scripts/${scriptId}/recordings`, {
      method: 'POST',
      credentials: 'same-origin',
      body: fd,
    });
    if (!res.ok) {
      let message = 'Upload failed';
      try {
        const body = (await res.json()) as { error?: string };
        if (body.error) message = body.error;
      } catch {
        // ignore
      }
      throw new ApiError(res.status, message);
    }
    return (await res.json()) as { ok: true; id: string };
  },

  listRecordings: (scriptId: string) =>
    api<{ recordings: Recording[] }>(`/api/admin/scripts/${scriptId}/recordings`),

  /** Single-row read used by transcription polling — includes the
   *  transcript blob and last error message. */
  getRecording: (recordingId: string) =>
    api<{ recording: RecordingDetail }>(`/api/admin/recordings/${recordingId}`),

  /** Kicks off Whisper transcription. Returns 202 immediately while the
   *  actual work runs in a background waitUntil; poll getRecording to see
   *  status flip to 'transcribed' or 'failed'. */
  transcribeRecording: (recordingId: string) =>
    api<{ ok: true; status: 'transcribing' }>(
      `/api/admin/recordings/${recordingId}/transcribe`,
      { method: 'POST', body: JSON.stringify({}) },
    ),

  /** (Re)generate the AI paper-edit analysis. `template_override`
   *  optionally re-analyses the take as a different video type than the
   *  script's own template_key. Returns 202; poll getRecording to watch
   *  paper_edit_status flip to 'ready' or 'failed'. */
  regeneratePaperEdit: (
    recordingId: string,
    templateOverride: string | null,
  ) =>
    api<{ ok: true; status: 'analysing' }>(
      `/api/admin/recordings/${recordingId}/paper-edit`,
      {
        method: 'POST',
        body: JSON.stringify({ template_override: templateOverride }),
      },
    ),

  deleteRecording: (recordingId: string) =>
    api<{ ok: true }>(`/api/admin/recordings/${recordingId}`, {
      method: 'DELETE',
    }),

  /** URL the inline <audio src=...> element points at. */
  recordingAudioUrl: (recordingId: string) =>
    `/api/admin/recordings/${recordingId}/audio`,

  // ---------- Script bundles ("Links") ----------

  listBundles: (opts?: { view?: 'active' | 'archived' }) =>
    api<{ bundles: AdminBundleSummary[] }>(
      `/api/admin/bundles?view=${opts?.view === 'archived' ? 'archived' : 'active'}`,
    ),

  createBundle: (body: {
    title: string;
    client_name?: string | null;
    kind: BundleKind;
    intro_note?: string | null;
    client_email?: string | null;
    items: AdminBundleItemInput[];
  }) =>
    api<{ id: string; slug: string }>('/api/admin/bundles', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  getBundle: (id: string) => api<AdminBundleDetail>(`/api/admin/bundles/${id}`),

  updateBundle: (
    id: string,
    body: {
      title?: string;
      client_name?: string | null;
      kind?: BundleKind;
      intro_note?: string | null;
      client_email?: string | null;
      items?: AdminBundleItemInput[];
    },
  ) =>
    api<AdminBundleDetail>(`/api/admin/bundles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  rotateBundleSlug: (id: string) =>
    api<{ ok: true; slug: string }>(`/api/admin/bundles/${id}/rotate-slug`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  archiveBundle: (id: string, archived: boolean) =>
    api<{ ok: true; archived: boolean }>(`/api/admin/bundles/${id}/archive`, {
      method: 'POST',
      body: JSON.stringify({ archived }),
    }),

  deleteBundle: (id: string) =>
    api<{ ok: true }>(`/api/admin/bundles/${id}`, { method: 'DELETE' }),

  // 400 {error:'no_client_email'|'email_not_configured'|'no_ready_scripts'},
  // 409 {error:'already_sent', ready_emailed_at} and 502 {error:'send_failed'}
  // all surface as ApiError — inspect err.status / err.body at the call site
  // to drive the force-resend confirm flow.
  sendBundleReadyEmail: (id: string, opts?: { force?: boolean }) =>
    api<{ ok: true }>(`/api/admin/bundles/${id}/send-ready-email`, {
      method: 'POST',
      body: JSON.stringify(opts?.force ? { force: true } : {}),
    }),
};

/** Cooperative edit-lock fields sent with suggestion writes (Feature L). */
export type SuggestionLock = { lock_token: string; author_name: string };

export const clientApi = {
  getScript: (slug: string) => api<PublicScript>(`/api/client/scripts/${slug}`),

  // Script bundles ("Links") — Phase 3 client hub + switcher.
  getBundle: (slug: string) => api<ClientBundle>(`/api/client/bundles/${slug}`),

  listComments: (slug: string) =>
    api<{ threads: CommentThread[]; script_doc_version: number; published_round?: number | null }>(
      `/api/client/scripts/${slug}/comments`,
    ),
  createThread: (slug: string, body: CreateThreadRequest) =>
    api<{ ok: true; thread_id: string }>(`/api/client/scripts/${slug}/comments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  reply: (slug: string, threadId: string, body: AddReplyRequest) =>
    api<{ ok: true; id: string }>(
      `/api/client/scripts/${slug}/comments/${threadId}/reply`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  resolveComment: (slug: string, threadId: string) =>
    api<{ ok: true }>(`/api/client/scripts/${slug}/comments/${threadId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  reopenComment: (slug: string, threadId: string) =>
    api<{ ok: true }>(`/api/client/scripts/${slug}/comments/${threadId}/reopen`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  // General notes (C10). Every call carries this browser's device token so
  // the server can say which notes are "mine" and gate edit and remove.
  listNotes: (slug: string) =>
    api<{ notes: PublicNoteEntry[] }>(`/api/client/scripts/${slug}/notes`, {
      headers: { 'X-HSP-Device': getOrCreateDeviceToken() },
    }),
  // `id` is generated by the page so a retried add lands once.
  addNote: (slug: string, note: { id: string; author_name: string; body: string }) =>
    api<{ note: PublicNoteEntry }>(`/api/client/scripts/${slug}/notes`, {
      method: 'POST',
      headers: { 'X-HSP-Device': getOrCreateDeviceToken() },
      body: JSON.stringify(note),
    }),
  updateNote: (slug: string, noteId: string, body: string) =>
    api<{ note: PublicNoteEntry }>(`/api/client/scripts/${slug}/notes/${noteId}`, {
      method: 'PATCH',
      headers: { 'X-HSP-Device': getOrCreateDeviceToken() },
      body: JSON.stringify({ body }),
    }),
  deleteNote: (slug: string, noteId: string) =>
    api<{ ok: true }>(`/api/client/scripts/${slug}/notes/${noteId}`, {
      method: 'DELETE',
      headers: { 'X-HSP-Device': getOrCreateDeviceToken() },
    }),

  // Suggested edits — public client endpoints
  getActiveSuggestion: (slug: string, lockToken: string) =>
    api<{ suggestion: PublicSuggestion | null }>(`/api/client/scripts/${slug}/suggestion`, {
      headers: { 'X-Suggestion-Lock': lockToken },
    }),
  startSuggestion: (slug: string, lock: SuggestionLock) =>
    api<{ suggestion: PublicSuggestion }>(`/api/client/scripts/${slug}/suggestion`, {
      method: 'POST',
      body: JSON.stringify(lock),
    }),
  updateSuggestion: (
    slug: string,
    doc_json: string,
    expected_version: number,
    lock: SuggestionLock,
  ) =>
    api<{ ok: true; edit_version: number }>(`/api/client/scripts/${slug}/suggestion`, {
      method: 'PATCH',
      body: JSON.stringify({ doc_json, expected_version, ...lock }),
    }),
  submitSuggestion: (
    slug: string,
    body: {
      author_name: string;
      note: string;
      doc_json: string;
      lock_token: string;
      expected_version: number;
    },
  ) =>
    api<{ ok: true; edit_version: number }>(`/api/client/scripts/${slug}/suggestion/submit`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  resumeSuggestion: (slug: string, lock: SuggestionLock) =>
    api<{ ok: true }>(`/api/client/scripts/${slug}/suggestion/resume`, {
      method: 'POST',
      body: JSON.stringify(lock),
    }),
  suggestionHeartbeat: (slug: string, lock: SuggestionLock) =>
    api<{ ok: true }>(`/api/client/scripts/${slug}/suggestion/heartbeat`, {
      method: 'POST',
      body: JSON.stringify(lock),
    }),

  // `email` is the optional "for a confirmation receipt" field on the Send
  // modal (Phase B3) — not stored anywhere, just used for that one
  // best-effort receipt email server-side.
  lockFeedback: (slug: string, author_name: string, note?: string, email?: string) =>
    api<{ ok: true; feedback_submitted_at: number }>(
      `/api/client/scripts/${slug}/lock-feedback`,
      { method: 'POST', body: JSON.stringify({ author_name, note: note ?? '', email: email ?? '' }) },
    ),

  approveScript: (slug: string, name: string) =>
    api<{ ok: true; status: 'approved'; approved_at: number; approved_by_name: string }>(
      `/api/client/scripts/${slug}/approve`,
      { method: 'POST', body: JSON.stringify({ name }) },
    ),

  // Heartbeat for the "client viewing now" indicator. 204 No Content; we
  // don't bother parsing the body — fire-and-forget.
  heartbeat: (slug: string) =>
    fetch(`/api/client/scripts/${slug}/heartbeat`, {
      method: 'POST',
      credentials: 'same-origin',
    }).catch(() => {
      /* network errors are non-fatal — the next tick will retry */
    }),
};

// Add comment endpoints to admin namespace
export const adminCommentsApi = {
  list: (scriptId: string) =>
    api<{ threads: CommentThread[] }>(`/api/admin/scripts/${scriptId}/comments`),
  createThread: (scriptId: string, body: CreateThreadRequest) =>
    api<{ ok: true; thread_id: string }>(`/api/admin/scripts/${scriptId}/comments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  reply: (scriptId: string, threadId: string, body: AddReplyRequest) =>
    api<{ ok: true; id: string }>(
      `/api/admin/scripts/${scriptId}/comments/${threadId}/reply`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  resolve: (scriptId: string, threadId: string) =>
    api<{ ok: true }>(`/api/admin/scripts/${scriptId}/comments/${threadId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  reopen: (scriptId: string, threadId: string) =>
    api<{ ok: true }>(`/api/admin/scripts/${scriptId}/comments/${threadId}/reopen`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
};
